// File: /pages/SetupWizard.js

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation, Trans } from 'react-i18next'; 
import { useNotification } from '../contexts/NotificationContext'; 

import { Container, Stepper, Step, StepLabel, Box, Button, Typography } from '@mui/material';

import SetupWizardSteps from '../components/SetupWizard/Steps';
import { ReactComponent as Logo } from '../assets/images/bwossy-logo.svg';
import BwossyLogo from "../components/Layout/BwossyLogo"; 

import { useAppData } from "../hooks/useAppData";
import { useAppStore } from "../hooks/useAppStore";
import { useUserData } from "../hooks/useUserData";
import { useUserStore } from "../hooks/useUserStore";

const stepLabels = ['Soundboard Layout', 'Select Words', 'Review'];

export default function SetupWizard() {
    const [stepIndex, setStepIndex] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);

    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showNotification } = useNotification();

    const {
      selectedWords, setSelectedWords,
      selectedLayout, setSelectedLayout,
      selectedShape, setSelectedShape,
      selectedSpacing, setSelectedSpacing,
      resetSetup,
    } = useAppStore();

    const { 
      updateSetupProgress,
      completeSetupWizard,
      skipSetupWizard
      } = useAppData();

    const { 
          baseURL,
          user, setUser
      } = useUserStore();
    
    const { 
      registerUser, 
      loginUser, 
      userQuery,
      updateAccount,
      updatePassword,
      refreshToken, 
      checkSession, 
      validateToken,
      reset
      } = useUserData();

    const {
      userQuery: { data: userData, isPending: isUserLoading },
    } = useUserData({
      onSuccessUIHandlers: {
        onUserFetchSuccess: (data) => {
          console.log('User fetched for Setup Wizard:', data);
          setUser(data);
        },
      },
      onErrorUIHandlers: {
        onUserFetchError: () => {
          showNotification({
            notification: t('errors.snackbar.UserDataFetchFailed'),
            type: 'error',
          });
        },
      },
    });

    const stepLabels = t('setupWizard.steps', { returnObjects: true });
    const StepComponent = SetupWizardSteps[stepIndex];

    const isStepValid = () => {
      switch (stepIndex) {
        case 0: // LayoutStep
          return !!selectedLayout && !!selectedShape && !!selectedSpacing;
        case 1: // WordSelectionStep
          return selectedWords && Object.keys(selectedWords).length > 0;
        default:
          return true;
      }
    };


    const handleNext = async () => {
      const isLastStep = stepIndex === SetupWizardSteps.length - 1;
      const progress = {};

      if (stepIndex >= 0) {
        progress.selectedLayout = selectedLayout;
        progress.selectedShape = selectedShape;
        progress.selectedSpacing = selectedSpacing;
      }
    
      if (stepIndex === 1 && selectedWords && Object.keys(selectedWords).length > 0) {
        progress.selectedWords = selectedWords;
      }
    
      setIsSaving(true);
      try {
        await updateSetupProgress.mutateAsync(progress);

        if (isLastStep) {
          await completeSetupWizard.mutateAsync();

          navigate('/MainContent');

        } else {
          setStepIndex(stepIndex + 1);
        }
      } catch (error) {
        console.error("Failed to save setup progress", error);
      } finally {
        setIsSaving(false);
      }
    };
  

    const handleBack = () => {
      if (stepIndex > 0) {
        setStepIndex(stepIndex - 1);
      }
    };
  
    const handleSkip = async () => {
      try {
        await skipSetupWizard.mutateAsync();
        navigate('/MainContent');
      } catch (err) {
        showNotification({
          notification: t('errors.snackbar.FailedToSkipWizard'),
          type: 'error',
        });
      }
    };

    useEffect(() => {
      if (isUserLoading || hasInitialized) return;
    
      if (user && !user.setupProgress) {
        navigate('/MainContent');
        return;
      }
    
      const { setupProgress = {} } = user;
      const { selectedWords = {}, selectedLayout, selectedShape, selectedSpacing } = setupProgress;
    
      const hasSelectedWords = Object.values(selectedWords).some(
        (arr) => Array.isArray(arr) && arr.length > 0
      );
    
      switch (true) {
        case !!selectedLayout && !!selectedShape && !!selectedSpacing && hasSelectedWords:
          setStepIndex(2);
          break;
        case !!selectedLayout && !!selectedShape && !!selectedSpacing:
          setStepIndex(1);
          break;
        default:
          setStepIndex(0);
      }
    
      if (selectedLayout) setSelectedLayout(selectedLayout);
      if (selectedShape) setSelectedShape(selectedShape);
      if (selectedSpacing) setSelectedSpacing(selectedSpacing);
      if (selectedWords) setSelectedWords(selectedWords);

    
      setHasInitialized(true);
    }, [user, isUserLoading, hasInitialized]);


    return (
      <div className="setup-wizard-background">

        <Container maxWidth="lg">
             <div className="setup-wizard-container">
               <div className="login-logo-container">
                   <span className="login-logo-name">
                     <Trans i18nKey="common.appNameLogo" components={[<BwossyLogo />]} />
                   </span>
               </div>
              </div>
              {stepIndex !== SetupWizardSteps.length - 1 && (
                <Typography
                    variant="h4"
                    align="center"
                    sx={{
                        fontWeight: 700,
                        fontFamily: 'var(--font-primary)',
                        color: 'var(--setup-wizard-title)',
                        mb: 4,
                    }}
                    >
                    {t('setupWizard.pageTitle')}
                    </Typography>
              )}

              <Stepper activeStep={stepIndex} alternativeLabel sx={{ mb: 6 }}>
                {stepLabels.map((label, index) => (
                  <Step key={label} completed={stepIndex > index}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              
  
          <StepComponent />
  
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
          <Button
            onClick={handleBack}
            sx={{ visibility: stepIndex === 0 ? 'hidden' : 'visible' }}
        >
            {t('setupWizard.back')}
        </Button>
        

            {stepIndex !== SetupWizardSteps.length - 1  && (
            <Typography
                variant="body2"
                color="white"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={handleSkip}
              >
                {t('setupWizard.skip')}
              </Typography>
            )}
            <Button 
              variant="contained" 
              onClick={handleNext}
              disabled={!isStepValid() || isSaving}
            >
              {stepIndex === SetupWizardSteps.length - 1
                ? t('setupWizard.finish')
                : t('setupWizard.next')}
            </Button>
          
          </Box>
        </Container>
      </div>
    );
  }